import React from "react";

class JackpotSlider extends React.Component {
    rootRef = React.createRef();
    trackRef = React.createRef();
    prevRef = React.createRef();
    nextRef = React.createRef();

    state = {
        i: 0,               // index สไลด์ปัจจุบัน
        jackpots: [],       // เก็บเป็น "หน่วยย่อย" (เช่น สตางค์) เพื่อทำทศนิยม 2 ตำแหน่ง
    };

    // === settings ===
    autoMs = 3500;        // เวลา auto slide
    peek = 250;           // px โชว์สไลด์ถัดไป
    tickerMs = 1200;      // เวลาอัปเดตเลข

    // ทศนิยม
    DECIMALS = 2;
    SCALE = 10 ** this.DECIMALS;

    // เริ่มที่ "หลักแสน"
    minStart = 100000;          // 100,000
    maxCap = 9999999.99;        // เพดาน 9,999,999.99

    // ช่วงสุ่มที่เพิ่มต่อครั้ง (หน่วยบาท)
    tickerStepMin = 120;        // ขั้นต่ำ +120
    tickerStepMax = 9800;       // ขั้นสูงสุด +9,800

    // timers
    _autoTimer = null;
    _tickerTimer = null;

    componentDidMount() {
        this.initJackpots();
        this.initSlider();
        this.startAuto();
        this.startTicker();
        window.addEventListener("resize", this.updateTransform);
    }

    componentDidUpdate(prevProps) {
        const prevLen = (prevProps.listitallgame || []).length;
        const currLen = (this.props.listitallgame || []).length;
        if (prevLen !== currLen) {
            this.initJackpots();
            this.setState({ i: 0 }, this.updateTransform);
        }
    }

    componentWillUnmount() {
        this.stopAuto();
        this.stopTicker();
        window.removeEventListener("resize", this.updateTransform);
        if (this.prevRef.current) this.prevRef.current.onclick = null;
        if (this.nextRef.current) this.nextRef.current.onclick = null;
    }

    // ---------- utils ----------
    randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

    // แปลงค่าที่เก็บ (หน่วยย่อย) → ตัวเลขทศนิยมปกติ
    centsToNumber = (cents) => (cents ?? 0) / this.SCALE;

    // format เป็น string มีคอมม่า + ทศนิยม 2 ตำแหน่ง
    formatAmount = (cents) =>
        this.centsToNumber(cents).toLocaleString("en-US", {
            minimumFractionDigits: this.DECIMALS,
            maximumFractionDigits: this.DECIMALS,
        });

    // ---------- Slider core ----------
    getN = () => (this.props.listitallgame || []).length;

    getGapPx = () => {
        const track = this.trackRef.current;
        if (!track) return 16;
        const g = getComputedStyle(track).gap || "16px";
        const num = parseFloat(String(g).replace(/[^\d.]/g, "")) || 16;
        return num;
    };

    getSlideW = () => {
        const root = this.rootRef.current;
        if (!root) return 0;
        return root.clientWidth - this.peek;
    };

    updateTransform = () => {
        const track = this.trackRef.current;
        if (!track) return;
        const { i } = this.state;
        const gap = this.getGapPx();
        const slideW = this.getSlideW();
        const offset = i * (slideW + gap);
        track.style.transform = `translateX(${-offset}px)`;
    };

    show = (k) => {
        const N = this.getN();
        if (!N) return;
        const i = ((k % N) + N) % N;
        this.setState({ i }, this.updateTransform);
    };

    next = () => this.show(this.state.i + 1);
    prev = () => this.show(this.state.i - 1);

    initSlider = () => {
        if (this.prevRef.current)
            this.prevRef.current.onclick = (e) => {
                e.preventDefault();
                this.prev();
            };
        if (this.nextRef.current)
            this.nextRef.current.onclick = (e) => {
                e.preventDefault();
                this.next();
            };
        const root = this.rootRef.current;
        if (root) {
            root.addEventListener("mouseenter", this.stopAuto);
            root.addEventListener("mouseleave", this.startAuto);
        }
        this.show(0);
    };

    startAuto = () => {
        if (this._autoTimer) return;
        this._autoTimer = setInterval(this.next, this.autoMs);
    };
    stopAuto = () => {
        if (this._autoTimer) {
            clearInterval(this._autoTimer);
            this._autoTimer = null;
        }
    };

    // ---------- Jackpot ticker (เก็บเป็นหน่วยย่อย) ----------
    initJackpots = () => {
        const N = this.getN();
        const minC = Math.floor(this.minStart * this.SCALE);
        const maxInitC = Math.floor(
            Math.min(this.minStart * 8, this.maxCap) * this.SCALE
        );
        const jackpots = Array.from({ length: N }, () =>
            this.randomInt(minC, maxInitC)
        );
        this.setState({ jackpots });
    };

    startTicker = () => {
        if (this._tickerTimer) return;
        this._tickerTimer = setInterval(() => {
            this.setState((prev) => {
                const arr = [...prev.jackpots];
                const capC = Math.floor(this.maxCap * this.SCALE);
                for (let idx = 0; idx < arr.length; idx++) {
                    // สุ่ม step เป็น “บาท” แล้วแปลงเป็นหน่วยย่อย
                    const stepBaht = this.randomInt(this.tickerStepMin, this.tickerStepMax);
                    const stepC = stepBaht * this.SCALE;
                    let next = (arr[idx] ?? 0) + stepC;

                    if (next >= capC) {
                        // ถ้าแตะเพดานแล้ว reset กลับมาแถวๆ หลักแสนใหม่ และเดินต่อ
                        const resetMin = Math.floor(this.minStart * this.SCALE);
                        const resetMax = Math.floor(this.minStart * 2 * this.SCALE);
                        next = this.randomInt(resetMin, resetMax);
                    }
                    arr[idx] = next;
                }
                return { jackpots: arr };
            });
        }, this.tickerMs);
    };

    stopTicker = () => {
        if (this._tickerTimer) {
            clearInterval(this._tickerTimer);
            this._tickerTimer = null;
        }
    };

    // ---------- Render ----------
    render() {
        const { listitallgame = [] } = this.props;
        const { jackpots, i: activeIndex } = this.state;

        return (
            <div
                id="jackpot_slider"
                className="JackpotGamesContainer"
                ref={this.rootRef}
            >
                <button
                    className="jp-prev jp-btn jackpot-slide-arrow slide-prev"
                    ref={this.prevRef}
                >
                    ‹
                </button>
                <button
                    className="jp-next jp-btn jackpot-slide-arrow slide-next"
                    ref={this.nextRef}
                >
                    ›
                </button>

                <div className="JackpotGames" ref={this.trackRef}>
                    {listitallgame.map((listitem, idx) => (
                        <div
                            className={`item ${activeIndex === idx ? "is-active" : ""}`}
                            key={idx}
                            style={{ flex: `0 0 calc(100% - ${this.peek}px)` }}
                        >
                            <div className="left">
                                <a
                                    className="linkHelper_container"
                                    onClick={(e) =>
                                        this.props.onOpenGame?.(e, listitem.gameId, listitem.partner)
                                    }
                                >
                                    <div className="openGame">
                                        <div>
                                            <span
                                                style={{
                                                    position: "relative",
                                                    display: "block",
                                                    padding: "61.8911% 0 0",
                                                }}
                                            >
                                                <img
                                                    alt={listitem.gameId}
                                                    src={listitem.image}
                                                    decoding="async"
                                                    style={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        display: "block",
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        border: "none",
                                                        margin: "auto",
                                                    }}
                                                />
                                            </span>
                                        </div>
                                        <div className="GameName">{listitem.description}</div>
                                    </div>
                                </a>
                            </div>

                            <div className="right">
                                {/* ใช้ formatAmount เพื่อให้มีคอมม่า + ทศนิยม 2 ตำแหน่ง */}
                                <ul className="box-item">
                                    {String(
                                        this.formatAmount(jackpots[idx] ?? Math.floor(this.minStart * this.SCALE))
                                    )
                                        .split("")
                                        .map((ch, k) => (
                                            <li
                                                key={k}
                                                className={/\d/.test(ch) ? "number-item" : "mark-item"}
                                            >
                                                <span className="comma">{ch}</span>
                                            </li>
                                        ))}
                                </ul>

                                <div className="JackpotName">
                                    <div className="DefaultTag">
                                        <span
                                            style={{
                                                backgroundColor: "rgb(40, 40, 178)",
                                                color: "rgb(255, 133, 25)",
                                            }}
                                        >
                                            {(listitem.partner || "").slice(0, 3).toUpperCase()}
                                        </span>
                                    </div>
                                    <span> {listitem.description}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

export default JackpotSlider;
